import { z } from "zod";
/**
 * Money, without credits. Every paid operation records one row in USD; account and run
 * totals are sums; an unknown cost stays unknown (never zero) and the total says so.
 * `operation_key` is unique per operation so a retried step can never bill twice.
 */
export const COST_KINDS = [
    "model",
    "hosted_tool",
    "platform_tool",
    "sandbox_time",
    "completion_check",
    "goal_help",
];
export const CostKindSchema = z.enum(COST_KINDS);
export const COST_SOURCES = ["provider_reported", "estimated", "unavailable"];
export const CostSourceSchema = z.enum(COST_SOURCES);
/** A USD amount as a decimal string with six places ("0.001234"); never a float. */
export const UsdSchema = z.string().regex(/^\d+\.\d{6}$/);
export const CostRecordSchema = z.object({
    id: z.string().min(1),
    accountId: z.string().min(1),
    runId: z.string().min(1).nullable(),
    /** `<kind>:<owner id>:<step or call id>` — the idempotency key of the paid operation. */
    operationKey: z.string().min(1),
    kind: CostKindSchema,
    /** Null only when `source` is `unavailable`. */
    usd: UsdSchema.nullable(),
    source: CostSourceSchema,
    createdAt: z.string().datetime(),
});
/** What a run or an account shows: the sum, and whether any record is still unknown. */
export const CostSummarySchema = z.object({
    totalUsd: UsdSchema,
    records: z.number().int().nonnegative(),
    /** True when at least one record is `unavailable`; the display says "incomplete". */
    incomplete: z.boolean(),
});
/**
 * The price book for estimates (V2's model_pricing, in USD per million tokens). The
 * provider's reported cost wins when it exists; the book prices what the provider does
 * not report. Periods are contiguous per model and the last one is open-ended.
 */
export const ModelPriceSchema = z.object({
    modelId: z.string().min(1),
    usdPerMtok: z.object({
        input: UsdSchema,
        output: UsdSchema,
        cacheWrite: UsdSchema,
        cacheRead: UsdSchema,
    }),
    effectiveFrom: z.string().datetime(),
    effectiveUntil: z.string().datetime().nullable(),
});
/** Format a float as the six-place decimal string the records carry (rounded half up). */
export function usd(amount) {
    if (!Number.isFinite(amount) || amount < 0)
        throw new Error(`invalid USD amount ${amount}`);
    return (Math.round(amount * 1_000_000) / 1_000_000).toFixed(6);
}
//# sourceMappingURL=costs.js.map